import React from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import {
  ArrowLeft,
  Map,
  Pencil,
  Circle,
  Trash2,
  Search,
} from "lucide-react";

const STEPS = [
  {
    icon: Map,
    title: "Center the Map",
    desc: "Pan and zoom to the area you want to target. The map automatically opens centered on your assigned county and state.",
  },
  {
    icon: Pencil,
    title: "Draw a Free-Form Area",
    desc: "Select the polygon tool from the map toolbar, then click around the area you want to search. Click the starting point again to complete the shape.",
  },
  {
    icon: Circle,
    title: "Draw a Radius",
    desc: "Select the circle tool, click your desired center point, then drag outward to define the search radius.",
  },
  {
    icon: Trash2,
    title: "Clear the Shape",
    desc: 'Use the "Clear Shape" button on the map anytime you want to remove the current area and start over.',
  },
  {
    icon: Search,
    title: "Apply Filters & Search",
    desc: "Add optional filters like price, status, or date from the sidebar, then click Search to view leads inside your selected area.",
  },
];

export default function MapHelpPage() {
  const navigate = useNavigate();

  return (
    <AppShell>
      <div className="min-h-full bg-white">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <button
            type="button"
            onClick={() => navigate("/leads")}
            className="group inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-zinc-950 transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            Back to Filters
          </button>

          <div className="mb-10">
           

            <h1
              className="text-4xl sm:text-5xl font-black tracking-tight text-zinc-950 mb-4"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Search Leads Using the Map
            </h1>

            <p className="text-lg text-zinc-600 leading-relaxed max-w-3xl">
              Draw custom search areas directly on the map to find leads within
              specific neighborhoods, streets, or target zones using polygons
              and radius searches.
            </p>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-zinc-200 bg-zinc-50 shadow-sm mb-14">
            <img
              src="https://leads-backend.s3.us-west-1.amazonaws.com/map_illustration.gif"
              alt="Map drawing tool walkthrough"
              className="w-full object-cover max-h-[720px]"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {STEPS.map((step, i) => {
              const Icon = step.icon;

              return (
                <div
                  key={step.title}
                  className="rounded-3xl border border-zinc-200 bg-white p-6 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-2xl bg-zinc-950 text-white flex items-center justify-center">
                        <Icon className="h-5 w-5" />
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-bold tracking-wide text-zinc-400">
                          STEP {i + 1}
                        </span>

                        <div className="h-px flex-1 bg-zinc-100" />
                      </div>

                      <h2 className="text-lg font-bold text-zinc-950 mb-2">
                        {step.title}
                      </h2>

                      <p className="text-sm leading-7 text-zinc-600">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AppShell>
  );
}